<template>
  <li
    :class="{
      'is-match': isMatch,
      'is-dim': isDim,
      'is-top-level': isTopLevel,
    }"
  >
    <div
      class="org-node"
      role="button"
      tabindex="0"
      @click="$emit('open', node)"
      @keydown.enter.prevent="$emit('open', node)"
    >
      <div class="org-node__avatar">
        <img
          v-if="node.avatar_url"
          :src="node.avatar_url"
          :alt="node.full_name"
        />
        <span v-else>{{ initials }}</span>
      </div>
      <div class="org-node__body">
        <div class="org-node__name" :title="node.full_name">
          {{ node.full_name || "Employee" }}
        </div>
        <div class="org-node__meta" :title="node.designation">
          {{ node.designation || "No designation" }}
        </div>
        <div class="org-node__code">{{ node.employee_id }}</div>
        <div v-if="node.manager_name" class="org-node__manager">
          <i class="ti ti-arrow-up-right me-1"></i>{{ node.manager_name }}
        </div>
        <div v-else-if="!isTopLevel" class="org-node__manager org-node__manager--none">
          No manager set
        </div>
      </div>
      <button
        v-if="hasReports"
        type="button"
        class="org-node__toggle"
        :title="localCollapsed ? 'Show direct reports' : 'Hide direct reports'"
        @click.stop="toggleCollapse"
      >
        <i
          class="ti"
          :class="localCollapsed ? 'ti-chevron-down' : 'ti-chevron-up'"
        ></i>
        <span>{{ node.reports.length }}</span>
      </button>
    </div>

    <ul v-if="hasReports && !effectiveCollapsed" class="org-children">
      <org-tree-node
        v-for="child in node.reports"
        :key="child.id"
        :node="child"
        :search="search"
        :force-collapse="forceCollapse"
        @open="$emit('open', $event)"
        @layout-change="$emit('layout-change')"
      />
    </ul>
  </li>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import OrgTreeNode from "@/components/hrm/org-tree-node.vue";

defineOptions({ name: "OrgTreeNode" });

const props = defineProps({
  node: { type: Object, required: true },
  search: { type: String, default: "" },
  forceCollapse: { type: Boolean, default: false },
});

const emit = defineEmits(["open", "layout-change"]);

const localCollapsed = ref(false);

watch(
  () => props.forceCollapse,
  (value) => {
    localCollapsed.value = !!value;
  },
  { immediate: true }
);

const hasReports = computed(
  () => Array.isArray(props.node?.reports) && props.node.reports.length > 0
);

const effectiveCollapsed = computed(
  () => props.forceCollapse || localCollapsed.value
);

const isTopLevel = computed(() => !props.node?.manager_id);

const needle = computed(() => String(props.search || "").trim().toLowerCase());

const isMatch = computed(() => {
  if (!needle.value) return false;
  const hay = [
    props.node?.full_name,
    props.node?.first_name,
    props.node?.last_name,
    props.node?.employee_id,
    props.node?.designation,
    props.node?.manager_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle.value);
});

const isDim = computed(() => !!needle.value && !isMatch.value);

const initials = computed(() => {
  const first = String(props.node?.first_name || "").trim().charAt(0);
  const last = String(props.node?.last_name || "").trim().charAt(0);
  return (first + last || "?").toUpperCase();
});

function toggleCollapse() {
  localCollapsed.value = !localCollapsed.value;
  emit("layout-change");
}
</script>

<style scoped>
li {
  list-style: none;
  text-align: center;
  position: relative;
  padding: 1rem 0.5rem 0;
  flex: 0 0 auto;
}

.org-node {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.55rem;
  width: 190px;
  max-width: 100%;
  padding: 0.65rem 0.75rem;
  border-radius: 0.75rem;
  background: #fff;
  border: 1px solid #e6e8ef;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  text-align: left;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.org-node:hover {
  border-color: #fe8a22;
  box-shadow: 0 8px 20px rgba(254, 138, 34, 0.14);
}

.is-match > .org-node {
  border-color: #fe8a22;
  box-shadow: 0 0 0 2px rgba(254, 138, 34, 0.2);
}

.is-dim > .org-node {
  opacity: 0.45;
}

.is-top-level > .org-node {
  border-color: #dbeafe;
  background: #f8fbff;
}

.org-node__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #fff4ea;
  color: #fe8a22;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.org-node__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.org-node__body {
  min-width: 0;
  flex: 1;
}

.org-node__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-node__meta,
.org-node__code {
  font-size: 0.7rem;
  color: #6b7280;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-node__manager {
  margin-top: 0.2rem;
  font-size: 0.68rem;
  color: #2563eb;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-node__manager--none {
  color: #9ca3af;
}

.org-node__toggle {
  position: absolute;
  right: 0.35rem;
  top: 0.35rem;
  border: 0;
  background: #eef2ff;
  border-radius: 999px;
  font-size: 0.65rem;
  padding: 0.12rem 0.35rem;
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  color: #4338ca;
}

.org-children {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 0.5rem 1rem;
  padding-top: 1.35rem;
  position: relative;
  margin: 0 auto;
  max-width: 100%;
}

.org-children::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 1.35rem;
  background: #cbd5e1;
  transform: translateX(-50%);
}

.org-children > li::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: #cbd5e1;
}

.org-children > li::after {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 1rem;
  background: #cbd5e1;
  transform: translateX(-50%);
}

.org-children > li:first-child::before {
  left: 50%;
}

.org-children > li:last-child::before {
  right: 50%;
}

.org-children > li:only-child::before {
  display: none;
}
</style>
